// FILE: src/components/modals/FavoritesModal.jsx

import React from 'react';
import { Heart, Plus, ArrowLeft, Sparkles, TrendingUp, BadgePercent, Tag, Gift } from 'lucide-react';
import { getProductImage } from '../../utils/imageHelpers';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDiscountAmount = (product) => {
  const regularPrice = toNumber(product?.price);
  const salePrice = toNumber(product?.sale_price);

  if (salePrice > 0 && regularPrice > salePrice) {
    return Math.round(regularPrice - salePrice);
  }

  return 0;
};

const getEffectivePrice = (product) => {
  const regularPrice = toNumber(product?.price);
  const salePrice = toNumber(product?.sale_price);

  if (salePrice > 0 && regularPrice > 0 && salePrice < regularPrice) {
    return salePrice;
  }

  return regularPrice;
};

const getDiscountPercent = (product) => {
  const regularPrice = toNumber(product?.price);
  const salePrice = toNumber(product?.sale_price);

  if (salePrice > 0 && regularPrice > salePrice) {
    return Math.round(((regularPrice - salePrice) / regularPrice) * 100);
  }

  return 0;
};

const getFallbackUpscaleMessage = (product) => {
  const effectivePrice = getEffectivePrice(product);

  if (effectivePrice >= 250) {
    return 'Premium upgrade: combine with a weekly subscription and save 12%.';
  }

  if (effectivePrice >= 120) {
    return 'Family value offer: add 2 units and unlock delivery savings.';
  }

  return 'Smart saver offer: add 3 units from this line and save 8%.';
};

const getPromotionMessage = (product) => {
  const backendMessage = [
    product?.promotion_message,
    product?.promotionMessage,
    product?.promo_message,
    product?.promoMessage,
    product?.offer_message,
    product?.offerMessage,
    product?.marketing_message,
    product?.marketingMessage,
  ].find((value) => typeof value === 'string' && value.trim().length > 0);

  if (backendMessage) {
    return backendMessage.trim();
  }

  return getFallbackUpscaleMessage(product);
};

const FavoritesModal = ({
  isOpen,
  onClose,
  favorites,
  setFavorites,
  toggleFavorite,
  products = [],
  addToCart,
  showToast,
  appTheme = 'blue',
}) => {
  if (!isOpen) return null;

  const isMidnight = appTheme === 'midnight';
  const isWhite = appTheme === 'white';

  const favoritesList = Array.isArray(favorites) ? favorites : [];
  const favoriteIdSet = new Set(
    favoritesList
      .map((item) => (item?.id === undefined || item?.id === null ? '' : String(item.id)))
      .filter(Boolean)
  );

  const promotionSuggestions = Array.isArray(products)
    ? products
      .filter((item) => item && item.id !== undefined && item.id !== null)
      .filter((item) => !favoriteIdSet.has(String(item.id)))
      .sort((left, right) => getDiscountAmount(right) - getDiscountAmount(left))
      .slice(0, 3)
    : [];

  const removeFavorite = (product) => {
    if (typeof toggleFavorite === 'function') {
      toggleFavorite(product);
      return;
    }

    if (typeof setFavorites === 'function') {
      setFavorites(favoritesList.filter((item) => String(item.id) !== String(product.id)));
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    showToast(`${product.name} added to cart!`, 'success');
  };

  const pageBg = isMidnight ? 'bg-slate-900' : 'bg-gray-50';
  const cardBg = isMidnight ? 'bg-slate-800' : isWhite ? 'glass-card-white-theme' : 'bg-white';
  const cardHeaderBg = isMidnight
    ? 'bg-gradient-to-br from-indigo-900/60 via-violet-900/40 to-slate-800 border-indigo-700'
    : 'bg-gradient-to-br from-gray-100 via-indigo-50 to-sky-100 border-gray-200';
  const titleText = isMidnight ? 'text-slate-100' : 'text-gray-800';
  const subText = isMidnight ? 'text-slate-400' : 'text-indigo-700';
  const mutedText = isMidnight ? 'text-slate-500' : 'text-gray-400';
  const imgWrapBg = isMidnight ? 'bg-slate-700/80' : 'bg-white shadow-md';
  const dealBoosterBg = isMidnight
    ? 'bg-gradient-to-r from-green-900/50 via-emerald-900/40 to-slate-800 border-green-700'
    : 'bg-gradient-to-r from-green-100 via-emerald-50 to-teal-100 border-green-300 shadow-sm';
  const dealBoosterLabel = isMidnight ? 'text-green-300' : 'text-gray-900';
  const dealBoosterDesc = isMidnight ? 'text-emerald-300' : 'text-gray-900';
  const dealBoosterPromo = isMidnight ? 'text-rose-300' : 'text-rose-900';
  const upscaleSectionBg = isMidnight
    ? 'bg-slate-800 border-slate-700'
    : 'bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 border-indigo-100';
  const upscaleTitle = isMidnight ? 'text-slate-100' : 'text-indigo-900';
  const upscaleBadge = isMidnight ? 'text-indigo-300 bg-slate-700 border-slate-600' : 'text-indigo-700 bg-white border-indigo-100';
  const upscaleCard = isMidnight ? 'bg-slate-700 border-slate-600' : 'bg-white/80 border-indigo-100';
  const upscaleCardTitle = isMidnight ? 'text-slate-100' : 'text-gray-800';
  const upscaleCardDesc = isMidnight ? 'text-rose-300' : 'text-rose-700';
  const upscaleCardPromo = isMidnight ? 'text-indigo-300' : 'text-indigo-700';
  const upscaleBtn = isMidnight ? 'text-indigo-300 bg-slate-600 hover:bg-slate-500' : 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200';
  const addonDivider = isMidnight ? 'border-slate-600' : 'border-indigo-200';
  const addonTitle = isMidnight ? 'text-slate-300' : 'text-cyan-800';
  const addonCard = isMidnight ? 'bg-slate-700 border-slate-600' : 'bg-white border-cyan-100';
  const addonCardTitle = isMidnight ? 'text-slate-100' : 'text-gray-800';
  const addonCardDesc = isMidnight ? 'text-slate-400' : 'text-gray-500';
  const addonBtn = isMidnight ? 'text-cyan-300 bg-slate-600 hover:bg-slate-500' : 'text-cyan-700 bg-cyan-100 hover:bg-cyan-200';
  const tipBg = isMidnight ? 'bg-slate-800 border-slate-700' : 'bg-pink-50 border-pink-200';
  const tipText = isMidnight ? 'text-slate-300' : 'text-gray-700';
  const removeHover = isMidnight ? 'hover:bg-slate-600' : 'hover:bg-red-50';

  return (
    <div className={`min-h-full pb-20 ${pageBg}`}>
      <div
        className="text-white p-6 rounded-b-3xl"
        style={appTheme === 'white'
          ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
          : { background: 'var(--header-gradient)', color: 'var(--text-light)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            title="Go back"
            style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
          >
            <ArrowLeft size={22} />
          </button>
          {favoritesList.length > 0 && (
            <span className="bg-white bg-opacity-20 text-white text-xs font-semibold px-3 py-1 rounded-full">
              {favoritesList.length} saved
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold">My Favorites</h1>
        <p className="opacity-80">Your saved products in one place</p>
      </div>

      <div className="p-4">

        {favoritesList.length === 0 ? (
          <div className="text-center py-12">
            <Heart size={64} className={`mx-auto mb-4 ${isMidnight ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={`text-lg ${subText}`}>No favorites yet</p>
            <p className={`text-sm mt-2 ${mutedText}`}>Heart products you love to save them here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {favoritesList.map((product) => (
              <div
                key={product.id}
                className={`${cardBg} rounded-xl shadow-sm overflow-hidden`}
              >
                <div className={`${cardHeaderBg} p-4 border-b`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`${imgWrapBg} rounded-xl p-3 shadow-sm flex items-center justify-center min-w-[72px]`}>
                        {getProductImage(product, 'large')}
                      </div>
                      <div className="min-w-0">
                        <h3 className={`font-semibold truncate ${titleText}`}>{product.name}</h3>
                        {product.description && (
                          <p className={`text-xs truncate ${subText}`}>{product.description}</p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-lg font-bold text-orange-500">₹{product.price}</span>
                          {product.fat && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              {product.fat} fat
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => removeFavorite(product)}
                      className={`text-red-500 ${removeHover} p-2 rounded-full transition-all`}
                      title="Remove from favorites"
                    >
                      <Heart size={20} fill="currentColor" />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className={`mb-3 rounded-xl border ${dealBoosterBg} px-3 py-3`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className={`inline-flex items-center gap-1.5 text-[12px] font-bold ${dealBoosterLabel}`}>
                        <BadgePercent size={15} />
                        Deal Booster
                      </div>
                      {getDiscountPercent(product) > 0 && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm ring-1 motion-safe:animate-pulse ${isMidnight ? 'bg-green-500 text-white ring-green-700' : 'bg-green-600 text-white ring-green-400'}`}>
                          {getDiscountPercent(product)}% OFF
                        </span>
                      )}
                    </div>
                    <p className={`text-xs font-semibold mt-1.5 flex items-center gap-1.5 ${dealBoosterDesc} drop-shadow-sm`}>
                      <Tag size={13} />
                      {getDiscountAmount(product) > 0
                        ? `Flash price drop: Save ₹${getDiscountAmount(product)} on ${product.name}.`
                        : `Combo saver: Pair ${product.name} with daily essentials for extra value.`}
                    </p>
                    <p className={`text-xs mt-1 ${dealBoosterPromo}`}>{getPromotionMessage(product)}</p>
                  </div>

                  <button
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-orange-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add Deal to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {favoritesList.length > 0 && (
          <div className={`mt-6 rounded-2xl border ${upscaleSectionBg} p-4`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className={isMidnight ? 'text-indigo-400' : 'text-indigo-600'} />
                <h3 className={`text-sm font-semibold ${upscaleTitle}`}>Upscale Promotions</h3>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border inline-flex items-center gap-1 ${upscaleBadge}`}>
                <BadgePercent size={12} />
                Limited
              </span>
            </div>

            <div className="space-y-2">
              {favoritesList.slice(0, 3).map((product) => (
                <div key={`promo-${product.id}`} className={`${upscaleCard} rounded-lg p-3 border`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${upscaleCardTitle}`}>{product.name}</p>
                      <p className={`text-[11px] mt-1 inline-flex items-center gap-1.5 font-semibold ${upscaleCardDesc}`}>
                        <Gift size={12} />
                        {getDiscountAmount(product) > 0
                          ? `Save ₹${getDiscountAmount(product)} with this favorite deal`
                          : 'Add now to unlock bundle-only savings'}
                      </p>
                      <p className={`text-xs mt-1 ${upscaleCardPromo}`}>{getPromotionMessage(product)}</p>
                    </div>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${upscaleBtn}`}
                    >
                      Add Deal
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {promotionSuggestions.length > 0 && (
              <div className={`mt-4 border-t ${addonDivider} pt-3`}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={15} className={isMidnight ? 'text-cyan-400' : 'text-cyan-700'} />
                  <p className={`text-xs font-semibold ${addonTitle}`}>Recommended add-ons</p>
                </div>
                <div className="space-y-2">
                  {promotionSuggestions.map((product) => (
                    <div key={`addon-${product.id}`} className={`${addonCard} rounded-lg border px-3 py-2 flex items-center justify-between gap-2`}>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${addonCardTitle}`}>{product.name}</p>
                        <p className={`text-xs inline-flex items-center gap-1.5 ${addonCardDesc}`}>
                          <Tag size={12} className={isMidnight ? 'text-cyan-400' : 'text-cyan-700'} />
                          {getDiscountAmount(product) > 0
                            ? `Save up to ₹${getDiscountAmount(product)} with this combo`
                            : 'Bundle suggestion for your current favorites'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${addonBtn}`}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {favoritesList.length > 0 && (
          <div className={`mt-6 border rounded-xl p-4 ${tipBg}`}>
            <p className={`text-sm ${tipText}`}>
              <span className="font-semibold">Tip:</span> Add your favorite items to cart for quick reordering.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesModal;
